var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

// cache
import Cache from './Cache';

// cache key
import ReactCacheKey from './ReactCacheKey';
import SerializedCacheKey from './SerializedCacheKey';
import StandardCacheKey from './StandardCacheKey';

// constants
import { DEFAULT_OPTIONS, FINITE_POSITIVE_INTEGER, FUNCTION_NAME_REGEXP, GOTCHA_OBJECT_CLASSES, INVALID_PROMISE_LIBRARY_ERROR, STATIC_PROPERTIES_TO_PASS } from './constants';

// serialize
import { getSerializerFunction } from './serialize';

// types


/**
 * @private
 *
 * @function isComplexObject
 *
 * @description
 * is the object passed a complex object
 *
 * @param {*} object object to test if it is complex
 * @returns {boolean} is it a complex object
 */
export var isComplexObject = function isComplexObject(object) {
  return !!object && typeof object === 'object';
};

/**
 * @private
 *
 * @function isFiniteAndPositiveInteger
 *
 * @description
 * is the number passed an integer that is finite and positive
 *
 * @param {number} number number to test for finiteness and positivity
 * @returns {boolean} is the number finite and positive
 */
export var isFiniteAndPositiveInteger = function isFiniteAndPositiveInteger(number) {
  return FINITE_POSITIVE_INTEGER.test('' + number);
};

/**
 * @private
 *
 * @function isFunction
 *
 * @description
 * is the object passed a function or not
 *
 * @param {*} object object to test
 * @returns {boolean} is it a function
 */
export var isFunction = function isFunction(object) {
  return typeof object === 'function';
};

/**
 * @private
 *
 * @function isPlainObject
 *
 * @description
 * is the object passed a plain object or not
 *
 * @param {*} object object to test
 * @returns {boolean} is it a plain object
 */
export var isPlainObject = function isPlainObject(object) {
  return isComplexObject(object) && object.constructor === Object;
};

/**
 * @private
 *
 * @function isValueObjectOrArray
 *
 * @description
 * check if the object is actually an object or array
 *
 * @param {*} object object to test
 * @returns {boolean} is the object an object or array
 */
export var isValueObjectOrArray = function isValueObjectOrArray(object) {
  if (!isComplexObject(object)) {
    return false;
  }

  var index = 0;

  while (index < GOTCHA_OBJECT_CLASSES.length) {
    if (object instanceof GOTCHA_OBJECT_CLASSES[index]) {
      return false;
    }

    index++;
  }

  return true;
};

/**
 * @private
 *
 * @function take
 *
 * @description
 * take the first N number of items from the array (faster than slice)
 *
 * @param {number} size the number of items to take
 * @returns {function(Array<*>): Array<*>} the shortened array
 */
export var take = function take(size) {
  return function (array) {
    if (size >= array.length) {
      return array;
    }

    switch (size) {
      case 0:
        return [];

      case 1:
        return [array[0]];

      case 2:
        return [array[0], array[1]];

      case 3:
        return [array[0], array[1], array[2]];

      case 4:
        return [array[0], array[1], array[2], array[3]];

      case 5:
        return [array[0], array[1], array[2], array[3], array[4]];
    }

    return array.slice(0, size);
  };
};

/**
 * @private
 *
 * @function addStaticPropertiesToFunction
 *
 * @description
 * add static properties to the memoized function if they exist on the original
 *
 * @param {function} originalFunction the function to be memoized
 * @param {function} memoizedFn the higher-order memoized function
 * @returns {function} memoizedFn with static properties added
 */
export var addStaticPropertiesToFunction = function addStaticPropertiesToFunction(originalFunction, memoizedFn) {
  var index = STATIC_PROPERTIES_TO_PASS.length,
      property = void 0;

  while (index--) {
    property = STATIC_PROPERTIES_TO_PASS[index];

    if (originalFunction[property]) {
      memoizedFn[property] = originalFunction[property];
    }
  }

  return memoizedFn;
};

/**
 * @private
 *
 * @function compose
 *
 * @description
 * method to compose functions and return a single function
 *
 * @param {...Array<function>} functions the functions to compose
 * @returns {function(...Array<*>): *} the composed function
 */
export var compose = function compose() {
  for (var _len = arguments.length, functions = Array(_len), _key = 0; _key < _len; _key++) {
    functions[_key] = arguments[_key];
  }

  return functions.reduce(function (f, g) {
    return function () {
      return f(g.apply(undefined, arguments));
    };
  });
};

/**
 * @private
 *
 * @function createCurriableOptionMethod
 *
 * @description
 * create a method that will curry moize with the option + value passed
 *
 * @param {function} fn the method to call
 * @param {string} option the name of the option to apply
 * @param {*} value the value to assign to option
 * @returns {function} the moizer with the option pre-applied
 */
export var createCurriableOptionMethod = function createCurriableOptionMethod(fn, option) {
  return function (value) {
    var _fn;

    return fn((_fn = {}, _fn[option] = value, _fn));
  };
};

/**
 * @private
 *
 * @function createFindIndex
 *
 * @description
 * create a findIndex method based on the startingIndex passed
 *
 * @param {number} startingIndex the index to start in the find method returned
 * @returns {function(Array<ListItem>, *): number} the findIndex method
 */
export var createFindIndex = function createFindIndex(startingIndex) {
  // eslint-disable-line no-use-before-define
  return function (list, key) {
    var index = startingIndex;

    while (index < list.length) {
      if (key === list[index].key) {
        return index;
      }

      index++;
    }

    return -1;
  };
};

/**
 * @private
 *
 * @function createPluckFromInstanceList
 *
 * @description
 * get a property from the list on the cache
 *
 * @param {{list: Array<Object>}} cache cache whose list to map over
 * @param {string} key key to pluck from list
 * @returns {Array<*>} array of values plucked at key
 */
export var createPluckFromInstanceList = function createPluckFromInstanceList(cache, key) {
  return function () {
    return cache.list.map(function (item) {
      return item[key];
    });
  };
};

/**
 * @private
 *
 * @function createPromiseRejecter
 *
 * @description
 * create method that will reject the promise and delete the key from cache
 *
 * @param {Cache} cache cache to update
 * @param {*} key key to delete from cache
 * @param {Options} options the options passed
 * @param {function} options.promiseLibrary the promise library used
 * @returns {function} the rejecter function for the promise
 */
export var createPromiseRejecter = function createPromiseRejecter(cache, key, options) {
  return function (exception) {
    cache.remove(key);

    return options.promiseLibrary.reject(exception);
  };
};

/**
 * @private
 *
 * @function createPromiseResolver
 *
 * @description
 * create method that will resolve the promise and update the key in cache
 *
 * @param {Cache} cache cache to update
 * @param {*} key key to update in cache
 * @param {boolean} hasMaxAge should the cache expire after some time
 * @param {Options} options the options passed
 * @param {number} options.maxAge the age after which the cache will be expired
 * @param {function} options.onExpire a callback that is called after removing the key
 * @param {function} options.promiseLibrary the promise library used
 * @returns {function} the resolver function for the promise
 */
export var createPromiseResolver = function createPromiseResolver(cache, key, hasMaxAge, options) {
  return function (resolvedValue) {
    cache.update(key, options.promiseLibrary.resolve(resolvedValue));

    if (hasMaxAge) {
      cache.expireAfter(key);
    }

    return resolvedValue;
  };
};

/**
 * @private
 *
 * @function findIndex
 *
 * @description
 * find the index of the key starting at the first index
 *
 * @param {Array<ListItem>} list the list to find the key in
 * @param {*} key the key to test against
 * @returns {number} the index of the matching key, or -1
 */
export var findIndex = createFindIndex(0);

/**
 * @private
 *
 * @function findIndexAfterFirst
 *
 * @description
 * find the index of the key starting at the second index
 *
 * @param {Array<ListItem>} list the list to find the key in
 * @param {*} key the key to test against
 * @returns {number} the index of the matching key, or -1
 */
export var findIndexAfterFirst = createFindIndex(1);

/**
 * @private
 *
 * @function getDefaultedOptions
 *
 * @description
 * get the options coalesced to their defaults
 *
 * @param {Object} options the options passed to the moize method
 * @returns {Options} the coalesced options object
 */
export var getDefaultedOptions = function getDefaultedOptions(options) {
  var coalescedOptions = _extends({}, DEFAULT_OPTIONS, options);

  return !coalescedOptions.serialize ? coalescedOptions : _extends({}, coalescedOptions, {
    serializer: getSerializerFunction(coalescedOptions)
  });
};

/**
 * @private
 *
 * @function getFunctionNameViaRegexp
 *
 * @description
 * use regexp match on stringified function to get the function name
 *
 * @param {function} fn function to get the name of
 * @returns {string} function name
 */
export var getFunctionNameViaRegexp = function getFunctionNameViaRegexp(fn) {
  // $FlowIgnore allow type conversion to string
  var match = ('' + fn).match(FUNCTION_NAME_REGEXP);

  return match ? match[1] : '';
};

/**
 * @private
 *
 * @function getFunctionName
 *
 * @description
 * get the function name, either from modern property or regexp match,
 * falling back to generic string
 *
 * @param {function} fn function to get the name of
 * @returns {string} function name
 */
export var getFunctionName = function getFunctionName(fn) {
  return fn.displayName || fn.name || getFunctionNameViaRegexp(fn) || 'Component';
};

/**
 * @private
 *
 * @function getKeyCount
 *
 * @description
 * get the count of keys in the object (faster than Object.keys().length)
 *
 * @param {Object} object the object to get the key count of
 * @returns {number} the count of keys
 */
export var getKeyCount = function getKeyCount(object) {
  var counter = 0;

  for (var ignored in object) {
    counter++;
  }

  return counter;
};

/**
 * @private
 *
 * @function getNewOrExistingCacheKey
 *
 * @description
 * get the cache key if it exists in cache, else a newly-constructed one
 *
 * @param {Cache} cache the cache to test against
 * @param {Array<*>} key the key to test if exists in the cache
 * @param {Function} Constructor the constructor function
 * @returns {CacheKey} the new or existing cache key
 */
export var getNewOrExistingCacheKey = function getNewOrExistingCacheKey(cache, key, Constructor) {
  // $FlowIgnore if cache has size, the key exists
  if (cache.size && cache.lastItem.key.matches(key)) {
    // $FlowIgnore if cache has size, the key exists
    return cache.lastItem.key;
  }

  var index = 1;

  while (index < cache.size) {
    // $FlowIgnore if cache matches, the key exists
    if (cache.list[index].key.matches(key)) {
      // $FlowIgnore if cache matches, the key exists
      return cache.list[index].key;
    }

    index++;
  }

  return new Constructor(key);
};

/**
 * @private
 *
 * @function getNewOrExistingCacheKeyCustomEquals
 *
 * @description
 * get the cache key if it exists in cache, else a newly-constructed one
 *
 * @param {Cache} cache the cache to test against
 * @param {Array<*>} key the key to test if exists in the cache
 * @param {Function} Constructor the constructor function
 * @param {function} isEqual the custom equality mnthod
 * @returns {CacheKey} the new or existing cache key
 */
export var getNewOrExistingCacheKeyCustomEquals = function getNewOrExistingCacheKeyCustomEquals(cache, key, Constructor, isEqual) {
  // $FlowIgnore if cache has size, the key exists
  if (cache.size && cache.lastItem.key.matchesCustom(key, isEqual)) {
    // $FlowIgnore if cache has size, the key exists
    return cache.lastItem.key;
  }

  var index = 1;

  while (index < cache.size) {
    // $FlowIgnore if cache matches custom, the key exists
    if (cache.list[index].key.matchesCustom(key, isEqual)) {
      // $FlowIgnore if cache matches custom, the key exists
      return cache.list[index].key;
    }

    index++;
  }

  return new Constructor(key);
};

/**
 * @private
 *
 * @function getConstructor
 *
 * @description
 * get the constructor type based on the options passed
 *
 * @param {Options} options the options passed to the moizer
 * @returns {Function} the constructor method
 */
export var getConstructor = function getConstructor(options) {
  if (options.isReact) {
    return ReactCacheKey;
  }

  if (options.serialize) {
    return SerializedCacheKey;
  }

  return StandardCacheKey;
};

/**
 * @private
 *
 * @function getTransform
 *
 * @description
 * get the transform method based on the options passed
 *
 * @param {Options} options the options to build the transform function from
 * @returns {function|undefined} the transform function
 */
export var getTransform = function getTransform(options) {
  var transform = options.transformArgs;

  if (isFiniteAndPositiveInteger(options.maxArgs)) {
    transform = transform ? compose(transform, take(options.maxArgs)) : take(options.maxArgs);
  }

  return !options.serialize ? transform : transform ? compose(options.serializer, transform) : options.serializer;
};

/**
 * @private
 *
 * @function createGetCacheKey
 *
 * @description
 * create the method that will get the cache key based on the options passed to the moized method
 *
 * @param {Cache} cache the cache to get the key from
 * @param {Options} options the options passed to the moized method
 * @returns {function(*): CacheKey} the method that will get the cache key
 */
export var createGetCacheKey = function createGetCacheKey(cache, options) {
  var Constructor = getConstructor(options);
  var transform = getTransform(options);

  if (options.equals) {
    if (transform) {
      return function (key) {
        return getNewOrExistingCacheKeyCustomEquals(cache, transform(key), Constructor, options.equals);
      };
    }

    return function (key) {
      return getNewOrExistingCacheKeyCustomEquals(cache, key, Constructor, options.equals);
    };
  }

  if (transform) {
    return function (key) {
      return getNewOrExistingCacheKey(cache, transform(key), Constructor);
    };
  }

  return function (key) {
    return getNewOrExistingCacheKey(cache, key, Constructor);
  };
};

/**
 * @private
 *
 * @function createSetNewCachedValue
 *
 * @description
 * assign the new value to the key in the functions cache and return the value
 *
 * @param {Cache} cache the cache to assign the value to at key
 * @param {Options} options the options passed to the moize method
 * @returns {function(function, *, *): *} value just stored in cache
 */
export var createSetNewCachedValue = function createSetNewCachedValue(cache, options) {
  var hasMaxAge = isFiniteAndPositiveInteger(options.maxAge);
  var hasMaxSize = isFiniteAndPositiveInteger(options.maxSize);

  if (options.isPromise) {
    if (!isFunction(options.promiseLibrary) && !isPlainObject(options.promiseLibrary)) {
      throw new TypeError(INVALID_PROMISE_LIBRARY_ERROR);
    }

    return function (key, value) {
      var handler = value.then(createPromiseResolver(cache, key, hasMaxAge, options), createPromiseRejecter(cache, key, options));

      cache.add(key, handler);

      if (hasMaxSize && cache.size > options.maxSize) {
        cache.remove(cache.list[cache.list.length - 1].key);
      }

      return handler;
    };
  }

  return function (key, value) {
    cache.add(key, value);

    if (hasMaxAge) {
      cache.expireAfter(key);
    }

    if (hasMaxSize && cache.size > options.maxSize) {
      cache.remove(cache.list[cache.list.length - 1].key);
    }

    return value;
  };
};

/**
 * @private
 *
 * @function splice
 *
 * @description
 * faster version of splicing a single item from the array
 *
 * @param {Array<*>} array array to splice from
 * @param {number} startingIndex index to splice at
 * @returns {Array<*>} array minus the item removed
 */
export var splice = function splice(array, startingIndex) {
  if (array.length) {
    var index = startingIndex - 1;

    while (++index < array.length) {
      array[index] = array[index + 1];
    }

    array.length -= 1;
  }

  return array;
};

/**
 * @private
 *
 * @function unshift
 *
 * @description
 * faster version of unshifting a single item into an array
 *
 * @param {Array<*>} array array to unshift into
 * @param {*} item item to unshift into array
 * @returns {*} the item just added to the array
 */
export var unshift = function unshift(array, item) {
  var index = array.length;

  while (index--) {
    array[index + 1] = array[index];
  }

  return array[0] = item;
};

/**
 * @private
 *
 * @function createAddPropertiesToFunction
 *
 * @description
 * add the static properties to the moized function
 *
 * @param {Cache} cache the cache for the moized function
 * @param {function} originalFunction the function to be moized
 * @param {Options} options the options passed to the moize method
 * @returns {function(function): function} the method which will add the static properties
 */
export var createAddPropertiesToFunction = function createAddPropertiesToFunction(cache, originalFunction, options) {
  return function (moizedFunction) {
    var getCacheKey = createGetCacheKey(cache, options);
    var setNewCachedValue = createSetNewCachedValue(cache, options);

    moizedFunction.cache = cache;
    moizedFunction.displayName = 'moize(' + getFunctionName(originalFunction) + ')';
    moizedFunction.isMoized = true;
    moizedFunction.options = options;
    moizedFunction.originalFunction = originalFunction;

    /**
     * @private
     *
     * @function add
     *
     * @description
     * manually add an item to cache if the key does not already exist
     *
     * @param {Array<any>} key key to use in cache
     * @param {*} value value to assign to key
     */
    moizedFunction.add = function (key, value) {
      var internalKey = getCacheKey(key);

      if (!cache.has(internalKey)) {
        setNewCachedValue(internalKey, value);
      }
    };

    /**
     * @private
     *
     * @function clear
     *
     * @description
     * clear the current cache for this method
     */
    moizedFunction.clear = function () {
      cache.clear();
    };

    /**
     * @private
     *
     * @function has
     *
     * @description
     * does the function have cache for the specific args passed
     *
     * @param {Array<*>} key combination of args to remove from cache
     * @returns {boolean} does the cache for the give args exist
     */
    moizedFunction.has = function (key) {
      return cache.has(getCacheKey(key));
    };

    /**
     * @private
     *
     * @function keys
     *
     * @description
     * get the list of keys currently in cache
     *
     * @returns {Array<*>}
     */
    moizedFunction.keys = createPluckFromInstanceList(cache, 'key');

    /**
     * @private
     *
     * @function remove
     *
     * @description
     * remove the item from cache for the key passed for this method
     *
     * @param {Array<*>} key combination of args to remove from cache
     */
    moizedFunction.remove = function (key) {
      cache.remove(getCacheKey(key));
    };

    /**
     * @private
     *
     * @function values
     *
     * @description
     * get the list of values currently in cache
     *
     * @returns {Array<*>}
     */
    moizedFunction.values = createPluckFromInstanceList(cache, 'value');

    return addStaticPropertiesToFunction(originalFunction, moizedFunction);
  };
};


//////////////////
// WEBPACK FOOTER
// ./~/filtermatrix/~/moize/es/utils.js
// module id = 208
// module chunks = 0